import { createSignal, For, Show } from "solid-js";
import type { Accessor } from "solid-js";
import type { Project } from "../lib/types";
import { Icon } from "./Icon";

export function ProjectNav(props: {
  projects: Accessor<Project[]>;
  activeProject: Accessor<string | null>;
  setProject: (id: string | null) => void;
  addProject: (name: string) => Promise<void>;
  updateProject: (id: string, patch: Partial<Pick<Project, "name" | "color">>) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;
  onNavigate?: () => void;
}) {
  const [name, setName] = createSignal("");
  const [adding, setAdding] = createSignal(false);
  const [editingId, setEditingId] = createSignal<string | null>(null);
  const [editName, setEditName] = createSignal("");
  const [editColor, setEditColor] = createSignal("#8f7a5c");
  const [confirmingDeleteId, setConfirmingDeleteId] = createSignal<string | null>(null);

  async function submit(event: SubmitEvent) {
    event.preventDefault();
    await props.addProject(name());
    setName("");
    setAdding(false);
  }

  function startEdit(project: Project) {
    setEditingId(project.id);
    setEditName(project.name);
    setEditColor(project.color);
    setConfirmingDeleteId(null);
  }

  async function saveEdit(event: SubmitEvent, project: Project) {
    event.preventDefault();
    await props.updateProject(project.id, {
      name: editName(),
      color: editColor(),
    });
    setEditingId(null);
    setConfirmingDeleteId(null);
  }

  async function removeProject(project: Project) {
    if (confirmingDeleteId() !== project.id) {
      setConfirmingDeleteId(project.id);
      return;
    }
    await props.deleteProject(project.id);
    setEditingId(null);
    setConfirmingDeleteId(null);
  }

  return (
    <section class="projects">
      <div class="section-heading">
        <span>Projects</span>
        <button class="small-icon-button" aria-label="Add project" onClick={() => setAdding(true)}>
          <Icon name="plus" size={16} />
        </button>
      </div>
      <div class="nav-stack">
        <For each={props.projects()}>
          {(project) => (
            <div
              class="project-row"
              classList={{ active: props.activeProject() === project.id }}
            >
              <Show
                when={editingId() !== project.id}
                fallback={
                  <form class="project-edit-form" onSubmit={(event) => void saveEdit(event, project)}>
                    <label class="sr-only" for={`project-edit-name-${project.id}`}>Project name</label>
                    <input
                      id={`project-edit-name-${project.id}`}
                      value={editName()}
                      onInput={(event) => setEditName(event.currentTarget.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Escape") {
                          setEditingId(null);
                          setConfirmingDeleteId(null);
                        }
                      }}
                      maxlength="80"
                    />
                    <label class="sr-only" for={`project-edit-color-${project.id}`}>Project color</label>
                    <input
                      id={`project-edit-color-${project.id}`}
                      type="color"
                      value={editColor()}
                      onInput={(event) => setEditColor(event.currentTarget.value)}
                    />
                    <button type="submit" class="ghost-button">Save</button>
                    <button
                      type="button"
                      class="ghost-button"
                      onClick={() => {
                        setEditingId(null);
                        setConfirmingDeleteId(null);
                      }}
                    >
                      Cancel
                    </button>
                    <button type="button" class="danger-button" onClick={() => void removeProject(project)}>
                      {confirmingDeleteId() === project.id ? "Confirm" : "Delete"}
                    </button>
                  </form>
                }
              >
                <button
                  class="nav-item project-pill"
                  classList={{ active: props.activeProject() === project.id }}
                  onClick={() => {
                    props.setProject(project.id);
                    props.onNavigate?.();
                  }}
                >
                  <span class="project-dot" style={{ "background-color": project.color }} aria-hidden="true" />
                  <span>{project.name}</span>
                </button>
                <button class="small-icon-button project-edit-button" aria-label={`Edit ${project.name}`} onClick={() => startEdit(project)}>
                  <Icon name="more" size={16} />
                </button>
              </Show>
            </div>
          )}
        </For>
      </div>
      <Show
        when={adding()}
        fallback={
          <button class="add-project-button" type="button" onClick={() => setAdding(true)}>
            <Icon name="plus" size={16} />
            Add project
          </button>
        }
      >
        <form class="project-form" onSubmit={submit}>
          <label class="sr-only" for="project-name">New project</label>
          <input
            id="project-name"
            value={name()}
            onInput={(event) => setName(event.currentTarget.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                setAdding(false);
                setName("");
              }
            }}
            placeholder="Project name"
            maxlength="80"
            autofocus
          />
          <div class="project-form-actions">
            <button type="submit" class="primary-button" disabled={!name().trim()}>Add</button>
            <button type="button" class="secondary-button" onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </form>
      </Show>
    </section>
  );
}
